import React, { useState, useMemo } from 'react'
import {
  SESSION_STATUS_GLOSSARY,
  LOGOUT_LOGIC_GLOSSARY,
} from '../constants/sessionGlossary'

export default function StatusMeaningModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('all') // 'all' | 'status' | 'logout'
  const [searchTerm, setSearchTerm] = useState('')

  const statusItems = useMemo(() => Object.values(SESSION_STATUS_GLOSSARY), [])
  const logoutItems = useMemo(() => Object.values(LOGOUT_LOGIC_GLOSSARY), [])

  const filteredStatuses = useMemo(() => {
    if (activeTab === 'logout') return []
    if (!searchTerm.trim()) return statusItems
    const term = searchTerm.toLowerCase()
    return statusItems.filter(
      (item) =>
        item.label.toLowerCase().includes(term) ||
        item.meaning.toLowerCase().includes(term) ||
        item.detail.toLowerCase().includes(term)
    )
  }, [activeTab, searchTerm, statusItems])

  const filteredLogouts = useMemo(() => {
    if (activeTab === 'status') return []
    if (!searchTerm.trim()) return logoutItems
    const term = searchTerm.toLowerCase()
    return logoutItems.filter(
      (item) =>
        item.label.toLowerCase().includes(term) ||
        item.meaning.toLowerCase().includes(term) ||
        item.detail.toLowerCase().includes(term)
    )
  }, [activeTab, searchTerm, logoutItems])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[320] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-on-surface/40 backdrop-blur-xs transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-3xl bg-surface-container-lowest border border-outline-variant/30 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-6 sm:p-7 border-b border-surface-container flex items-center justify-between gap-4 bg-surface-container-low/50">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-2xl">help</span>
            </span>
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-on-surface tracking-tight">
                Status & Meaning Legend
              </h2>
              <p className="text-xs text-on-surface-variant font-medium mt-0.5">
                Comprehensive dictionary of session integrity states and logout logic categories.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container-high transition"
            aria-label="Close modal"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Search & Tabs Toolbar */}
        <div className="px-6 py-3.5 border-b border-outline-variant/15 flex flex-wrap items-center justify-between gap-3 bg-surface-container-lowest">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition ${
                activeTab === 'all'
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              All Definitions
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('status')}
              className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition ${
                activeTab === 'status'
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              Session Status ({statusItems.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('logout')}
              className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition ${
                activeTab === 'logout'
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              Logout Logic ({logoutItems.length})
            </button>
          </div>

          <div className="flex items-center rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-1.5 w-full sm:w-64">
            <span className="material-symbols-outlined text-base text-on-surface-variant mr-2">search</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search meaning or status..."
              className="w-full bg-transparent text-xs text-on-surface outline-none placeholder:text-on-surface-variant/60"
            />
            {searchTerm ? (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="text-on-surface-variant hover:text-on-surface"
              >
                <span className="material-symbols-outlined text-sm">clear</span>
              </button>
            ) : null}
          </div>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-7 space-y-7">
          {/* Section A: Session Statuses */}
          {filteredStatuses.length > 0 && (
            <div>
              <div className="mb-3.5 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-primary" />
                <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary">
                  Session Lifecycle & Validation Statuses
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {filteredStatuses.map((item) => (
                  <div
                    key={item.key}
                    className="flex flex-col justify-between rounded-2xl border border-outline-variant/20 bg-surface-container-low/60 p-4 transition hover:bg-surface-container-low"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-black uppercase tracking-wider ${item.badgeClass || 'bg-surface-container text-on-surface'}`}>
                          {item.dotClass ? <span className={`h-2 w-2 rounded-full ${item.dotClass}`} /> : null}
                          {item.label}
                        </span>
                        <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                          {item.category}
                        </span>
                      </div>
                      <p className="text-xs font-bold text-on-surface leading-snug">
                        {item.meaning}
                      </p>
                      <p className="mt-1.5 text-[11px] text-on-surface-variant leading-relaxed">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section B: Logout Logic */}
          {filteredLogouts.length > 0 && (
            <div>
              <div className="mb-3.5 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-secondary" />
                <h3 className="text-xs font-black uppercase tracking-[0.14em] text-secondary">
                  Logout Logic & Session Termination Categories
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {filteredLogouts.map((item) => (
                  <div
                    key={item.key}
                    className="flex flex-col justify-between rounded-2xl border border-outline-variant/20 bg-surface-container-low/60 p-4 transition hover:bg-surface-container-low"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${item.badgeClass}`}>
                          {item.icon ? (
                            <span className="material-symbols-outlined text-sm">{item.icon}</span>
                          ) : null}
                          {item.label}
                        </span>
                      </div>
                      <p className="text-xs font-bold text-on-surface leading-snug">
                        {item.meaning}
                      </p>
                      <p className="mt-1.5 text-[11px] text-on-surface-variant leading-relaxed">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filteredStatuses.length === 0 && filteredLogouts.length === 0 && (
            <div className="py-12 text-center text-sm text-on-surface-variant">
              No matching statuses found for "{searchTerm}".
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 px-6 border-t border-outline-variant/15 flex items-center justify-between bg-surface-container-low/40 text-xs text-on-surface-variant">
          <span>Tip: Hover over the info icon beside any status in the table for an instant reminder.</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-primary px-5 py-2 font-bold text-on-primary hover:bg-primary/90 transition"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}
